import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import CatalogHeader from '../components/sections/CatalogHeader';
import PetCard from '../components/PetCard';
import Pagination from '../components/Pagination';
import LoadingMascot from '../components/LoadingMascot';
import { usePets } from '../hooks/usePets';
import { scrollBehavior } from '../utils/motion';
import { readRegimes, regimeValueOf, regimeToUrlValue } from '../utils/regimeFilter';
import type { AgeBandValue } from '../components/Filters/AgeBandFilter';
import type { RegimeFilterValue } from '../components/Filters/RegimeFilter';
import type { PetTypeBE, SearchPetsParams, PetKind, LifeStage } from '../types';
import '../styles/AdotarCatalog.css';

interface AdotarCatalogProps {
  kind: PetKind;
  chapter: string;
  title: string;
  subtitle: string;
}

const PAGE_SIZE = 12;

const AGE_BANDS: AgeBandValue[] = ['all', 'filhote', 'jovem', 'adulto', 'senior'];

function readAgeBand(raw: string | null): AgeBandValue {
  if (!raw) return 'all';
  const lc = raw.toLowerCase();
  return (AGE_BANDS as string[]).includes(lc) ? (lc as AgeBandValue) : 'all';
}

function readPage(raw: string | null): number {
  const n = raw == null ? 1 : parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return n;
}

const AdotarCatalog: React.FC<AdotarCatalogProps> = ({
  kind, chapter, title, subtitle,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const ageBand = readAgeBand(searchParams.get('idade'));
  const selectedRegimes = readRegimes(searchParams.get('regime'));
  const regime = regimeValueOf(selectedRegimes);
  const humanPage = readPage(searchParams.get('page'));
  const apiPage = humanPage - 1; // backend is 0-based

  const params: SearchPetsParams = {
    type: (kind === 'cat' ? 'CAT' : 'DOG') as PetTypeBE,
    page: apiPage,
    size: PAGE_SIZE,
    // Send ageBand as uppercase LifeStage name when a real band is selected;
    // omit entirely for 'all'/'desconhecida' so the usePets cache key stays stable.
    ...(ageBand !== 'all' && ageBand !== 'desconhecida'
      ? { ageBand: ageBand.toUpperCase() as LifeStage }
      : {}),
    // Omit the key entirely for 'todos' so it never reaches the request and the
    // usePets cache key stays stable (an empty array would survive stripEmpty).
    ...(selectedRegimes.length ? { regimes: selectedRegimes } : {}),
  };

  const { pets, totalPages, totalElements, loading, error } = usePets(params);

  // Scroll back to the catalog top when paginating, but not when filter
  // changes (the page itself stays scrolled at the header naturally).
  useEffect(() => {
    if (humanPage > 1) {
      const el = document.querySelector('.catalog-header');
      el?.scrollIntoView({ behavior: scrollBehavior('smooth'), block: 'start' });
    }
  }, [humanPage]);

  const handleAgeChange = (next: AgeBandValue) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') {
      params.delete('idade');
    } else {
      params.set('idade', next);
    }
    params.delete('page'); // any filter change resets to page 1
    setSearchParams(params, { replace: false });
  };

  const handleRegimeChange = (next: RegimeFilterValue) => {
    const params = new URLSearchParams(searchParams);
    const urlValue = regimeToUrlValue(next);
    if (urlValue === null) {
      params.delete('regime');
    } else {
      params.set('regime', urlValue);
    }
    params.delete('page'); // any filter change resets to page 1
    setSearchParams(params, { replace: false });
  };

  const handlePageChange = (nextApiPage: number) => {
    const params = new URLSearchParams(searchParams);
    const human = nextApiPage + 1;
    if (human <= 1) params.delete('page');
    else params.set('page', String(human));
    setSearchParams(params, { replace: false });
  };

  return (
    <div className="adotar-catalog">
      <CatalogHeader
        chapter={chapter}
        title={title}
        subtitle={subtitle}
        count={loading ? undefined : totalElements}
        ageBand={ageBand}
        onAgeBandChange={handleAgeChange}
        regime={regime}
        onRegimeChange={handleRegimeChange}
      />

      {loading && (
        <LoadingMascot
          className="adotar-catalog__placeholder"
          label="a folhear o caderno…"
        />
      )}

      {error && !loading && (
        <div className="adotar-catalog__error" role="alert">
          <strong>Algo correu mal a abrir o caderno.</strong>
          <p>Tenta de novo daqui a um bocado, ou liga-nos diretamente.</p>
        </div>
      )}

      {!loading && !error && pets.length === 0 && (
        <div className="adotar-catalog__empty">
          <div className="adotar-catalog__empty-kind">— uma nota</div>
          {ageBand === 'all' && regime === 'todos' ? (
            <p>Não há fichas {kind === 'cat' ? 'de gatos' : 'de cães'} abertas neste momento.</p>
          ) : (
            <p>
              Nada com estes filtros hoje.{' '}
              {ageBand !== 'all' && (
                <button type="button" onClick={() => handleAgeChange('all')}>Ver todas as idades</button>
              )}
              {ageBand !== 'all' && regime !== 'todos' && ' '}
              {regime !== 'todos' && (
                <button type="button" onClick={() => handleRegimeChange('todos')}>Ver todos os regimes</button>
              )}
            </p>
          )}
        </div>
      )}

      {!loading && !error && pets.length > 0 && (
        <>
          <div className="adotar-catalog__grid">
            {pets.map((pet) => (
              <PetCard key={pet.id} pet={pet} />
            ))}
          </div>

          <Pagination
            page={apiPage}
            totalPages={totalPages}
            onChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

export default AdotarCatalog;
